import { useCallback, useEffect, useState } from "react";
import InventoryPicker from "@/components/InventoryPicker";
import PrescriptionSlip from "@/components/PrescriptionSlip";
import { toast } from "sonner";
import { Receipt, RefreshCw, Plus, Trash2, Send, Printer, Phone, Calendar, Search, Zap, AlertTriangle, FileText } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import Portal from "@/components/Portal";

const DEFAULT_CONTACT = "+91 90000 00000";

export default function PharmacyBilling() {
  const { branch } = useAuth();
  const [queue, setQueue] = useState([]);
  const [selected, setSelected] = useState(null);
  const [slipFor, setSlipFor] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/queue/billing", { params: { branch_code: branch.code } });
      setQueue(data);
    } catch (err) {
      toast.error(formatApiError(err, "Could not load billing queue"));
    } finally {
      setLoading(false);
    }
  }, [branch?.code]);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="max-w-6xl mx-auto animate-fade-up" data-testid="pharmacy-billing-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-[#5C5652] mb-3 ornament-dot">
            Pharmacy billing · {branch.name}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-forest mb-1">
            Bills waiting to be generated
          </h1>
          <p className="text-[#5C5652]">
            Add consultation charges and prescribed items, then generate the bill — an SMS is sent to the patient automatically.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="billing-refresh"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-sand bg-white hover:bg-[#F9F6F0] text-sm font-semibold"
        >
          <RefreshCw className="h-4 w-4" strokeWidth={1.75} />
          Refresh
        </button>
        <Link
          to="/billing/direct"
          data-testid="billing-direct-cta"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#E5A93D] text-[#2A2624] text-sm font-semibold hover:brightness-95"
        >
          <Zap className="h-4 w-4" strokeWidth={2} />
          Direct bill (walk-in)
        </Link>
      </div>

      <div className="bg-white border border-sand rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-sm text-[#5C5652]">Loading…</div>
        ) : queue.length === 0 ? (
          <div className="p-10 text-center">
            <div className="font-display text-forest text-xl mb-1">No bills pending</div>
            <p className="text-sm text-[#5C5652]">Patients will appear here once the doctor sends them for billing.</p>
          </div>
        ) : (
          <ul className="divide-y divide-[#E8E3D9]" data-testid="billing-queue-list">
            {queue.map((p, i) => (
              <li
                key={p.id}
                className="flex items-center gap-4 px-6 py-4 hover:bg-[#F9F6F0]"
              >
                <div className="h-10 w-10 rounded-full bg-[#E5A93D]/20 text-[#8A5F1B] flex items-center justify-center font-bold">
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#2A2624]">{p.name}</div>
                  <div className="text-xs text-[#5C5652]">
                    Token {p.op_token} · seen by {p.assigned_doctor_name || "—"}
                  </div>
                </div>
                <button
                  onClick={() => setSlipFor(p)}
                  data-testid={`open-slip-${p.mrn}`}
                  className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-sand hover:bg-[#F9F6F0] text-sm font-semibold text-[#2A2624]"
                  title="Print prescription slip"
                >
                  <FileText className="h-4 w-4" strokeWidth={1.75} />
                  Slip
                </button>
                <button
                  onClick={() => setSelected(p)}
                  data-testid={`open-bill-${p.mrn}`}
                  className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-forest text-white text-sm font-semibold hover:bg-forest-hover"
                >
                  <Receipt className="h-4 w-4" strokeWidth={1.75} />
                  Generate bill
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {selected && (
        <BillingModal
          patient={selected}
          onClose={() => setSelected(null)}
          onSaved={() => {
            setSelected(null);
            load();
          }}
        />
      )}
      {slipFor && (
        <PrescriptionSlip
          patient={slipFor}
          branch={branch}
          onClose={() => setSlipFor(null)}
        />
      )}
    </div>
  );
}

function todayPlusDays(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function BillingModal({ patient, onClose, onSaved }) {
  const { branch } = useAuth();
  const rxItems = patient?.consultation?.prescription?.items || [];
  const [items, setItems] = useState(() => {
    const base = [{ name: "Consultation fee", qty: 1, price: 300, sku: null }];
    rxItems.forEach((r) => {
      // Compose a helpful billing label so pharmacist sees dose + frequency at a glance
      const bits = [r.dose, r.frequency, r.duration].filter(Boolean).join(" · ");
      const label = bits ? `${r.drug} — ${bits}` : r.drug;
      base.push({
        name: label,
        qty: Number(r.qty) > 0 ? Number(r.qty) : 1,
        price: r.price != null ? Number(r.price) : 0,
        sku: r.sku || null,
      });
    });
    return base;
  });
  const [followUp, setFollowUp] = useState(todayPlusDays(7));
  const [contact, setContact] = useState(DEFAULT_CONTACT);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [pickerFor, setPickerFor] = useState(null);

  const total = items.reduce((s, it) => s + Number(it.qty || 0) * Number(it.price || 0), 0);
  const setItem = (i, patch) =>
    setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const addItem = () => setItems((prev) => [...prev, { name: "", qty: 1, price: 0, sku: null }]);
  const removeItem = (i) => setItems((prev) => prev.filter((_, idx) => idx !== i));

  const pickFromInv = (i, inv) => {
    setItem(i, { name: inv.name, price: inv.selling_price, sku: inv.sku });
    setPickerFor(null);
  };

  const submit = async () => {
    const clean = items
      .map((it) => ({ name: it.name.trim(), qty: Number(it.qty) || 0, price: Number(it.price) || 0, sku: it.sku || undefined }))
      .filter((it) => it.name && it.qty > 0);
    if (clean.length === 0) {
      toast.error("Add at least one line item");
      return;
    }
    if (!contact.trim()) {
      toast.error("Hospital contact number is required for the SMS");
      return;
    }
    // Hard-block negative prices — no scenario ever warrants them, and they
    // would silently reduce the total.
    const negatives = clean.filter(
      (it) => it.price < 0 && it.name.toLowerCase() !== "consultation fee"
    );
    if (negatives.length > 0) {
      toast.error(`Negative price on: ${negatives.map((it) => it.name).join(", ")}`);
      return;
    }
    // Warn before billing zero-priced medicines (typically legacy prescriptions
    // where the doctor didn't pick from stock — pharmacist should re-price them
    // before printing).
    const zeroPriced = clean.filter(
      (it) => it.price === 0 && it.name.toLowerCase() !== "consultation fee"
    );
    if (zeroPriced.length > 0) {
      const names = zeroPriced.map((it) => `• ${it.name}`).join("\n");
      const ok = window.confirm(
        `${zeroPriced.length} medicine${zeroPriced.length === 1 ? "" : "s"} in this bill ${zeroPriced.length === 1 ? "is" : "are"} priced ₹ 0:\n\n${names}\n\nContinue anyway (this will be billed as free)?`
      );
      if (!ok) return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/billing/${patient.id}`, {
        items: clean,
        follow_up_date: followUp,
        contact_number: contact,
        notes,
      });
      toast.success(`Bill Rs. ${data.bill.total.toFixed(2)} · SMS sent`);
      setReceipt(data);
    } catch (err) {
      toast.error(formatApiError(err, "Could not generate bill"));
    } finally {
      setBusy(false);
    }
  };

  if (receipt) {
    const b = receipt.bill || {};
    return (
      <ReceiptModal
        patient={receipt}
        bill={b}
        onClose={() => {
          onSaved && onSaved();
        }}
      />
    );
  }

  return (
    <Portal>
    <div
      className="fixed inset-0 bg-[#2A2624]/50 z-40 flex items-end sm:items-center justify-center p-0 sm:p-6"
      onClick={() => !busy && onClose()}
      data-testid="bill-modal"
    >
      <div
        className="bg-white w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-lg border border-sand animate-fade-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-sand px-6 py-4">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C5652] mb-1">
            {patient.mrn} · Token {patient.op_token}
          </div>
          <div className="font-display text-2xl text-forest">Bill for {patient.name}</div>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-bold uppercase tracking-wider text-[#5C5652]">
                Line items
              </div>
              <button
                onClick={addItem}
                data-testid="bill-add-item"
                className="text-sm text-forest font-semibold inline-flex items-center gap-1 hover:underline"
              >
                <Plus className="h-3.5 w-3.5" strokeWidth={2} /> Add row
              </button>
            </div>
            <div className="space-y-2" data-testid="bill-items">
              {items.map((it, i) => {
                const isConsult = (it.name || "").toLowerCase() === "consultation fee";
                const zeroWarn = !isConsult && (it.name || "").trim() && (Number(it.price) === 0);
                const negWarn = !isConsult && (it.name || "").trim() && (Number(it.price) < 0);
                const warn = zeroWarn || negWarn;
                return (
                <div key={i} className={`grid grid-cols-[2.5fr_0.7fr_1fr_auto_auto] gap-2 items-center ${warn ? "p-1 -m-1 rounded bg-[#C25946]/[0.06] ring-1 ring-[#C25946]/40" : ""}`} data-testid={`bill-row-${i}`}>
                  <div className="relative">
                    <input
                      data-testid={`bill-name-${i}`}
                      value={it.name}
                      title={it.name}
                      onChange={(e) => setItem(i, { name: e.target.value, sku: null })}
                      placeholder="Item or click 🔍 to pick from stock"
                      className={`h-10 pl-3 pr-8 w-full rounded-lg bg-white border ${it.sku ? "border-forest" : (warn ? "border-[#C25946]" : "border-sand")} focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 min-w-0`}
                    />
                    {it.sku && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] font-bold text-forest">📦</span>}
                    {!it.sku && warn && <AlertTriangle data-testid={`bill-zero-warn-${i}`} title={negWarn ? "Negative price" : "Zero price"} className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#C25946]" strokeWidth={2} />}
                  </div>
                  <input
                    data-testid={`bill-qty-${i}`}
                    type="number" min="1" value={it.qty}
                    onChange={(e) => setItem(i, { qty: e.target.value })}
                    className="h-10 px-2 rounded-lg bg-white border border-sand focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 min-w-0"
                  />
                  <input
                    data-testid={`bill-price-${i}`}
                    type="number" min="0" step="0.01" value={it.price}
                    onChange={(e) => setItem(i, { price: e.target.value })}
                    className={`h-10 px-2 rounded-lg bg-white border ${warn ? "border-[#C25946] text-[#C25946] font-semibold" : "border-sand"} focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 min-w-0`}
                  />
                  <button
                    onClick={() => setPickerFor(i)}
                    data-testid={`bill-pick-${i}`}
                    title="Pick from inventory"
                    className="h-10 w-10 rounded-lg border border-sand text-forest hover:bg-[#F9F6F0] flex items-center justify-center"
                  >
                    <Search className="h-4 w-4" strokeWidth={1.75} />
                  </button>
                  <button
                    onClick={() => removeItem(i)}
                    data-testid={`bill-remove-${i}`}
                    className="h-10 w-10 rounded-lg border border-sand text-[#5C5652] hover:bg-[#F9F6F0] flex items-center justify-center"
                  >
                    <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                  </button>
                </div>
              );})}
            </div>
            {pickerFor !== null && (
              <InventoryPicker branch={branch} onPick={(inv) => pickFromInv(pickerFor, inv)} onClose={() => setPickerFor(null)} />
            )}
            <div className="mt-3 flex justify-end">
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-[#5C5652]">Total</div>
                <div className="font-display text-3xl text-forest" data-testid="bill-total">
                  ₹ {total.toFixed(2)}
                </div>
              </div>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#5C5652] mb-2 flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" strokeWidth={1.75} /> Follow-up date
              </label>
              <input
                data-testid="bill-follow-up"
                type="date"
                value={followUp}
                onChange={(e) => setFollowUp(e.target.value)}
                className="w-full h-11 px-3 rounded-lg bg-white border border-sand focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#5C5652] mb-2 flex items-center gap-1">
                <Phone className="h-3.5 w-3.5" strokeWidth={1.75} /> Hospital contact
              </label>
              <input
                data-testid="bill-contact"
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                placeholder="+91 …"
                className="w-full h-11 px-3 rounded-lg bg-white border border-sand focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40"
              />
            </div>
          </div>

          <textarea
            data-testid="bill-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Notes (optional)"
            className="w-full px-3 py-3 rounded-lg bg-white border border-sand focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 resize-none"
          />

          <div className="p-4 rounded-lg bg-[#F9F6F0] border border-sand text-xs text-[#5C5652]">
            An SMS with token, bill amount, follow-up date and contact will be sent to the patient
            (<span className="font-semibold text-forest">{patient.phone}</span>). SMS is currently
            in <span className="font-semibold">mock mode</span> — messages are logged to the
            server, not delivered until MSG91 credentials are added.
          </div>

          <div className="flex gap-3 pt-2 border-t border-sand">
            <button
              onClick={submit}
              disabled={busy}
              data-testid="bill-submit"
              className="flex-1 h-12 rounded-lg bg-forest text-white font-semibold hover:bg-forest-hover disabled:opacity-60 inline-flex items-center justify-center gap-2"
            >
              <Send className="h-4 w-4" strokeWidth={1.75} />
              {busy ? "Generating…" : `Generate bill · ₹ ${total.toFixed(2)}`}
            </button>
            <button
              onClick={onClose}
              disabled={busy}
              className="h-12 px-5 rounded-lg border border-sand font-semibold hover:bg-[#F9F6F0]"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
    </Portal>
  );
}

function ReceiptModal({ patient, bill, onClose }) {
  const printA4 = () => {
    document.body.dataset.printMode = "a4";
    window.print();
    delete document.body.dataset.printMode;
  };
  const printThermal = () => {
    document.body.dataset.printMode = "thermal";
    window.print();
    delete document.body.dataset.printMode;
  };
  return (
    <Portal>
    <div
      className="fixed inset-0 bg-[#2A2624]/60 z-50 flex items-center justify-center p-6"
      onClick={onClose}
      data-testid="receipt-modal"
    >
      <div
        className="bg-white w-full max-w-md rounded-lg border border-sand overflow-hidden animate-fade-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-sand">
          <div className="flex items-center justify-center mb-3">
            <img src="https://customer-assets-cm19k8pv.emergentagent.net/job_ayurveda-care-27/artifacts/hi17r931_image-removebg-preview.png" alt="Badarinath" className="h-10 w-auto object-contain" />
          </div>
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#5C5652] mb-1">
              Pharmacy token
            </div>
            <div className="font-display text-6xl text-forest leading-none" data-testid="receipt-pharm-token">
              {bill.pharm_token}
            </div>
            <div className="mt-3 text-sm text-[#5C5652]">
              Bill total <span className="font-semibold text-forest">₹ {Number(bill.total).toFixed(2)}</span>
            </div>
          </div>
        </div>
        <div className="p-5 text-xs bg-[#F9F6F0] border-b border-sand" data-testid="receipt-sms-preview">
          <div className="font-bold uppercase tracking-wider text-[#5C5652] mb-2">
            SMS preview (mocked)
          </div>
          <pre className="whitespace-pre-wrap font-mono text-[#2A2624]">
{bill.sms_message}
          </pre>
        </div>
        <div className="p-5 flex gap-2 receipt-print-actions">
          <button
            onClick={printThermal}
            data-testid="receipt-print-thermal"
            className="flex-1 h-11 rounded-lg border border-sand font-semibold hover:bg-[#F9F6F0] inline-flex items-center justify-center gap-2 text-sm"
          >
            <Printer className="h-4 w-4" strokeWidth={1.75} />
            Thermal (80mm)
          </button>
          <button
            onClick={printA4}
            data-testid="receipt-print-a4"
            className="flex-1 h-11 rounded-lg border border-sand font-semibold hover:bg-[#F9F6F0] inline-flex items-center justify-center gap-2 text-sm"
          >
            <Printer className="h-4 w-4" strokeWidth={1.75} />
            A4 detailed
          </button>
          <button
            onClick={onClose}
            data-testid="receipt-close"
            className="flex-1 h-11 rounded-lg bg-forest text-white font-semibold hover:bg-forest-hover text-sm"
          >
            Done
          </button>
        </div>
        <style>{`
          @media print {
            body[data-print-mode="thermal"] { width: 80mm; }
            body[data-print-mode="thermal"] @page { size: 80mm auto; margin: 4mm; }
            body[data-print-mode="a4"] @page { size: A4; margin: 15mm; }
            .receipt-print-actions { display: none !important; }
          }
        `}</style>
      </div>
    </div>
    </Portal>
  );
}
